import React from "react";
import ReactModal from "react-modal";
import Loader from "../../../common/loader";
import { toast } from "react-toastify";
import { responseHandler, errorHandler } from "../../../requests/axios";
import Select from "../../../common/select";

// add more defaults as needed
const defaultFields = {
  text: "",
  password: "",
};

// Handle success in parent component
export default function GenericEditDialog({
  onSubmit,
  onSuccess,
  onClose,
  isOpen,
  title,
  fields,
  data,
  submitText = "Save",
  cancelText = "Cancel",
  successMessage = "",
  errorMessage = "",
}) {
  const [errors, setErrors] = React.useState({});
  const createFormFromFields = React.useCallback(() => {
    if (!fields.length || !data) {
      return {};
    }

    let newForm = {};
    fields.forEach((field) => {
      if (field.type === "password") {
        // special case for password
        newForm[field.name] = defaultFields[field.type];
      } else {
        newForm[field.name] = data[field.name];
      }
    });

    return newForm;
  }, [data, fields]);

  const [isLoading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState(createFormFromFields());

  const handleChange = React.useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  if (!data) {
    return;
  }

  function validateForm() {
    let passes = true;
    let newErrors = {};

    fields.forEach((field) => {
      if (field.required && !form[field.name]) {
        passes = false;
        newErrors[field.name] = ["This field is required"];
      }
    });

    setErrors(newErrors);
    return passes;
  }

  function formatApiErrors(errorObj) {
    let apiErrors = {};

    for (const key in errorObj) {
      if (errorObj.hasOwnProperty(key)) {
        apiErrors[key] = errorObj[key].join(". ");
      }
    }

    setErrors(apiErrors);
  }

  function beginSubmit() {
    setLoading(true);
    setErrors({});

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    onSubmit({ ...data, ...form })
      .then(
        responseHandler((response) => {
          toast.success(successMessage);
          onSuccess(response.data);
          onClose();
        })
      )
      .catch(
        errorHandler((error) => {
          console.error(error);

          if (error.status === 422) {
            formatApiErrors(
              error.response.data.errors || error.response.data.error
            );
          } else {
            toast.error(errorMessage);
          }
        })
      )
      .finally(() => setLoading(false));
  }

  return (
    <ReactModal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false}>
      <div className="v-react-modal__inner modal-width">
        <div className="v-react-modal__heading">{title}</div>
        <div className="v-react-modal__body">
          {fields.map((field, fIndex) => {
            switch (field.type) {
              case "text":
              case "password":
                return (
                  <div className="form-field" key={fIndex}>
                    <label>{field.label}</label>
                    <input
                      type={field.type}
                      name={field.name}
                      value={form[field.name]}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                    />
                    {errors.hasOwnProperty(field.name) ? (
                      <div className="form-field__error">
                        {errors[field.name]}
                      </div>
                    ) : null}
                  </div>
                );
              case "select":
                return (
                  <div className="form-field">
                    <label>{field.label}</label>
                    <Select
                      defaultValue={field.options.find(
                        (option) => form[field.name] === option.value
                      )}
                      options={field.options}
                      onChange={(option) =>
                        handleChange(field.name, option.value)
                      }
                      name={field.name}
                    />
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
        <div className="v-react-modal__footer" style={{ marginTop: "4px" }}>
          {isLoading ? (
            <Loader width={45} height={45} />
          ) : (
            <React.Fragment>
              <button className="btn btn--inverse-primary" onClick={onClose}>
                {cancelText}
              </button>
              <button className="btn btn--primary" onClick={beginSubmit}>
                {submitText}
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </ReactModal>
  );
}
